import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import {
  Layers, Plus, Play, Save, Trash2, ArrowDown, Filter, Table, Loader2,
  Shuffle, Columns, Type, SortAsc, X, Database, Wand2, ArrowLeft, Pencil, Sparkles,
  Calendar, LayoutGrid, List, Code2, Map
} from 'lucide-react';
import { toast } from 'sonner';
import { Separator } from '@/components/ui/separator';
import { VersionHistoryButton } from '@/components/VersionHistory';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { SaveWithVersionDialog } from '@/components/SaveWithVersionDialog';

import { STEP_TYPES, NODE_STYLES } from '@/components/data-prep/constants';
import { PipelineMiniMap } from '@/components/data-prep/PipelineMiniMap';
import { StepEditor } from '@/components/data-prep/StepEditor';

export default function DataPrepPage() {
  const [view, setView] = useState('list'); // 'list' or 'builder'
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  const [steps, setSteps] = useState([{ type: 'source', collection: '' }]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [collections, setCollections] = useState([]);
  const [dataSources, setDataSources] = useState([]);
  const [semanticModels, setSemanticModels] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [pipelineName, setPipelineName] = useState('');
  const [pipelineDesc, setPipelineDesc] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [addStepOpen, setAddStepOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  const isDirty = view === 'builder' && (pipelineName.trim().length > 0 || steps.length > 1 || (steps.length === 1 && steps[0].collection));
  const { confirmNavigation, UnsavedDialog } = useUnsavedChanges(isDirty, 'You have unsaved pipeline changes. Discard and leave?');
  const [fieldCache, setFieldCache] = useState({}); // { collectionName: [field1, field2, ...] }
  const [sourceFields, setSourceFields] = useState([]);
  const [joinFields, setJoinFields] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null); // index of selected step in canvas
  const [showPreview, setShowPreview] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const canvasRef = React.useRef(null);
  const nodeRefs = React.useRef({});

  const loadPipelines = async () => {
    setListLoading(true);
    try {
      const [c, p, ds, sm] = await Promise.all([api.getCollections(), api.listPrepPipelines(), api.getDataSources(), api.getSemanticModels().catch(() => [])]);
      setCollections(c.collections || []);
      setPipelines(p);
      setDataSources(ds);
      setSemanticModels(sm || []);
    } catch (e) { console.error(e); }
    finally { setListLoading(false); }
  };

  const introspectCollection = async (collectionName, target) => {
    if (!collectionName) return;
    if (fieldCache[collectionName]) {
      const fields = fieldCache[collectionName];
      if (target === 'source') setSourceFields(fields);
      else if (target === 'join') setJoinFields(fields);
      return;
    }
    try {
      const res = await api.introspectCollection(collectionName);
      const fields = (res.fields || []).map(f => f.name);
      setFieldCache(prev => ({ ...prev, [collectionName]: fields }));
      if (target === 'source') setSourceFields(fields);
      else if (target === 'join') setJoinFields(fields);
    } catch { /* field introspection is best-effort */ }
  };

  useEffect(() => { loadPipelines(); }, []);

  const addStep = (type) => { setSteps(prev => [...prev, { type }]); setAddStepOpen(false); };
  const updateStep = (idx, step) => setSteps(prev => prev.map((s, i) => i === idx ? step : s));
  const removeStep = (idx) => setSteps(prev => prev.filter((_, i) => i !== idx));

  const runAiSuggest = async () => {
    if (!aiPrompt.trim()) return;
    const srcColl = (steps.find(s => s.type === 'source') || {}).collection || '';
    setAiLoading(true);
    try {
      const res = await api.aiSuggestPrep(aiPrompt.trim(), srcColl);
      const newSteps = res?.steps || [];
      if (!newSteps.length) { toast.error(res?.explanation || 'No steps generated — try rephrasing.'); return; }
      setSteps(prev => [...prev, ...newSteps]);
      setAiPrompt('');
      toast.success(`Added ${newSteps.length} step${newSteps.length > 1 ? 's' : ''}${res?.explanation ? ' — ' + res.explanation : ''}`);
    } catch (e) {
      toast.error('AI suggestion failed');
    } finally { setAiLoading(false); }
  };

  const execute = async () => {
    setLoading(true);
    try {
      const res = await api.executePrep(steps, 200);
      setResult(res);
      if (res.error) toast.error(res.error);
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  const save = async (versionDesc) => {
    if (!pipelineName.trim()) { toast.error('Pipeline name required'); return; }
    setSaving(true);
    try {
      let pid = editingId;
      if (pid) {
        await api.updatePrepPipeline(pid, { name: pipelineName, description: pipelineDesc, steps });
      } else {
        const result = await api.savePrep(pipelineName, steps);
        pid = result.pipeline_id;
      }
      if (pid) await api.createVersion('prep_pipeline', pid, versionDesc || 'Saved');
      setSaveDialogOpen(false);
      toast.success('Pipeline saved & versioned');
      await loadPipelines();
      setView('list');
      resetBuilder();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const materialize = async () => {
    try {
      let data = result?.data;
      // If no result yet, run the pipeline first
      if (!data?.length) {
        toast.info('Running pipeline...');
        const res = await api.executePrep(steps, 1000);
        setResult(res);
        if (res.error) { toast.error(res.error); return; }
        data = res.data;
        if (!data?.length) { toast.error('Pipeline returned no data'); return; }
      }
      const res = await api.materializePrep({ data, name: pipelineName || 'prep_output', pipeline_id: editingId || '' });
      toast.success('Materialized as data source: ' + res.collection_name);
    } catch (err) { toast.error(err.message); }
  };

  const handleDelete = async (id) => {
    try {
      await api.deletePrepPipeline(id);
      toast.success('Pipeline deleted');
      setPipelines(prev => prev.filter(p => p.pipeline_id !== id));
      setDeleteConfirm(null);
    } catch (err) { toast.error(err.message); }
  };

  const openBuilder = (pipeline) => {
    if (pipeline) {
      setEditingId(pipeline.pipeline_id);
      setPipelineName(pipeline.name || '');
      setPipelineDesc(pipeline.description || '');
      setSteps(pipeline.steps || [{ type: 'source', collection: '' }]);
      // Load field schemas for source and join steps
      const srcStep = (pipeline.steps || []).find(s => s.type === 'source');
      if (srcStep?.collection) introspectCollection(srcStep.collection, 'source');
      const joinStep = (pipeline.steps || []).find(s => s.type === 'join');
      if (joinStep?.collection) introspectCollection(joinStep.collection, 'join');
    } else {
      resetBuilder();
    }
    setResult(null);
    setView('builder');
  };

  const resetBuilder = () => {
    setEditingId(null);
    setPipelineName('');
    setPipelineDesc('');
    setSteps([{ type: 'source', collection: '' }]);
    setResult(null);
    setSourceFields([]);
    setJoinFields([]);
  };

  // ─── Listing View ───
  if (view === 'list') {
    return (
      <div className="p-6 space-y-6" data-testid="data-prep-list">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold tracking-tight text-foreground">Data Preparation</h1>
            <p className="text-sm text-muted-foreground mt-1">Build and manage data transformation pipelines</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center rounded-lg border border-border/50 overflow-hidden">
              <button onClick={() => setViewMode('cards')} className={`p-1.5 ${viewMode === 'cards' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground'}`} data-testid="view-cards"><LayoutGrid className="w-4 h-4" /></button>
              <button onClick={() => setViewMode('table')} className={`p-1.5 ${viewMode === 'table' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground'}`} data-testid="view-table"><List className="w-4 h-4" /></button>
            </div>
            <Button onClick={() => openBuilder(null)} className="rounded-xl bg-primary text-primary-foreground shadow-[0_0_20px_rgba(59,130,246,0.3)] hover:shadow-[0_0_30px_rgba(59,130,246,0.5)] transition-all active:scale-95" data-testid="new-pipeline-btn">
              <Plus className="w-4 h-4 mr-2" /> New Pipeline
            </Button>
          </div>
        </div>

        {listLoading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>
        ) : pipelines.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="p-4 rounded-2xl bg-primary/5 border border-primary/10 mb-4"><Shuffle className="w-8 h-8 text-primary/40" /></div>
            <h3 className="text-base font-heading font-semibold text-foreground mb-1">No pipelines yet</h3>
            <p className="text-sm text-muted-foreground max-w-sm mb-4">Create your first data preparation pipeline to transform, clean, and join your data.</p>
            <Button onClick={() => openBuilder(null)} className="rounded-xl" data-testid="empty-new-pipeline-btn"><Plus className="w-4 h-4 mr-2" /> Create Pipeline</Button>
          </div>
        ) : viewMode === 'cards' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pipeline-cards">
            {pipelines.map(p => (
              <div key={p.pipeline_id} className="glass-card rounded-2xl p-5 hover:border-primary/20 hover:shadow-lg transition-all cursor-pointer group" onClick={() => openBuilder(p)} data-testid={`pipeline-card-${p.pipeline_id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="p-2.5 rounded-xl bg-indigo-500/10 border border-indigo-500/20">
                    <Shuffle className="w-5 h-5 text-indigo-400" />
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={e => { e.stopPropagation(); openBuilder(p); }} className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground" data-testid={`edit-pipeline-${p.pipeline_id}`}><Pencil className="w-3.5 h-3.5" /></button>
                    <span onClick={e => e.stopPropagation()}><VersionHistoryButton resourceType="prep_pipeline" resourceId={p.pipeline_id} resourceName={p.name} /></span>
                    <button onClick={e => { e.stopPropagation(); setDeleteConfirm(p.pipeline_id); }} className="p-1.5 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive" data-testid={`delete-pipeline-${p.pipeline_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                <h3 className="text-sm font-heading font-semibold text-foreground mb-1">{p.name}</h3>
                {p.description && <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{p.description}</p>}
                <div className="flex items-center gap-2 mt-3">
                  <Badge className="text-[9px] bg-primary/10 text-primary border-primary/20">{(p.steps || []).length} steps</Badge>
                  {p.steps?.[0]?.collection && <Badge variant="outline" className="text-[9px]">{p.steps[0].collection}</Badge>}
                </div>
                {p.created_at && <p className="text-[10px] text-muted-foreground/70 mt-2 flex items-center gap-1"><Calendar className="w-3 h-3" /> {p.created_at.substring(0, 10)}</p>}
              </div>
            ))}
          </div>
        ) : (
          <div className="glass-card rounded-2xl overflow-hidden" data-testid="pipeline-table">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border/50">
                <th className="px-4 py-3 text-left text-[10px] font-heading font-semibold text-muted-foreground uppercase">Name</th>
                <th className="px-4 py-3 text-left text-[10px] font-heading font-semibold text-muted-foreground uppercase">Steps</th>
                <th className="px-4 py-3 text-left text-[10px] font-heading font-semibold text-muted-foreground uppercase">Source</th>
                <th className="px-4 py-3 text-left text-[10px] font-heading font-semibold text-muted-foreground uppercase">Created</th>
                <th className="px-4 py-3 text-right text-[10px] font-heading font-semibold text-muted-foreground uppercase">Actions</th>
              </tr></thead>
              <tbody>
                {pipelines.map(p => (
                  <tr key={p.pipeline_id} className="border-b border-border/20 hover:bg-muted/10 cursor-pointer transition-colors" onClick={() => openBuilder(p)} data-testid={`pipeline-row-${p.pipeline_id}`}>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-semibold text-foreground">{p.name}</p>
                        {p.description && <p className="text-[10px] text-muted-foreground mt-0.5">{p.description}</p>}
                      </div>
                    </td>
                    <td className="px-4 py-3"><Badge className="text-[9px] bg-primary/10 text-primary border-primary/20">{(p.steps || []).length} steps</Badge></td>
                    <td className="px-4 py-3 text-xs text-muted-foreground font-mono">{p.steps?.[0]?.collection || '-'}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{p.created_at ? p.created_at.substring(0, 10) : '-'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={e => { e.stopPropagation(); openBuilder(p); }} className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground"><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={e => { e.stopPropagation(); setDeleteConfirm(p.pipeline_id); }} className="p-1.5 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-sm">
            <DialogHeader><DialogTitle className="font-heading">Delete Pipeline</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground mt-2">Are you sure? This action cannot be undone.</p>
            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="flex-1 rounded-lg">Cancel</Button>
              <Button variant="destructive" onClick={() => handleDelete(deleteConfirm)} className="flex-1 rounded-lg" data-testid="confirm-delete-btn">Delete</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ─── Builder View ───

  return (
    <div className="flex flex-col h-full" data-testid="data-prep-builder">
      {/* Top Bar */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border/50 bg-muted/10 flex-shrink-0">
        <Button variant="ghost" size="icon" onClick={() => confirmNavigation(() => { setView('list'); resetBuilder(); })} className="rounded-lg w-7 h-7" data-testid="back-to-list"><ArrowLeft className="w-4 h-4" /></Button>
        <Input value={pipelineName} onChange={e => setPipelineName(e.target.value)} placeholder="Pipeline name" className="rounded-lg bg-muted/50 border-border/50 text-sm w-60" data-testid="prep-name" />
        <Input value={pipelineDesc} onChange={e => setPipelineDesc(e.target.value)} placeholder="Description" className="rounded-lg bg-muted/50 border-border/50 text-[10px] h-8 w-48" data-testid="prep-desc" />
        <div className="flex items-center gap-1.5 ml-auto">
          <Button onClick={execute} disabled={loading} size="sm" className="rounded-lg text-xs gap-1.5" data-testid="run-prep-btn">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Run
          </Button>
          <Button variant="outline" size="sm" onClick={() => setSaveDialogOpen(true)} disabled={saving || !pipelineName.trim()} className="rounded-lg text-xs gap-1.5" data-testid="save-prep-btn"><Save className="w-3 h-3" /> Save</Button>
          <Button variant="outline" size="sm" onClick={materialize} disabled={!pipelineName.trim()} className="rounded-lg text-xs gap-1.5"><Database className="w-3 h-3" /> Materialize</Button>
          <Button variant={showPreview ? 'default' : 'outline'} size="sm" onClick={() => setShowPreview(!showPreview)} className="rounded-lg text-xs gap-1.5"><Table className="w-3 h-3" /> Preview</Button>
        </div>
      </div>

      {/* AI step generator */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-border/50 bg-primary/[0.03] flex-shrink-0">
        <Sparkles className="w-3.5 h-3.5 text-primary flex-shrink-0" />
        <Input
          value={aiPrompt}
          onChange={e => setAiPrompt(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !aiLoading) runAiSuggest(); }}
          placeholder="Describe a transform in plain English — e.g. 'bucket age into Young/Mid/Senior and add a running total of charges'"
          className="rounded-lg bg-muted/40 border-border/40 text-xs h-8 flex-1"
          data-testid="prep-ai-input"
        />
        <Button onClick={runAiSuggest} disabled={aiLoading || !aiPrompt.trim()} size="sm" className="rounded-lg text-xs gap-1.5" data-testid="prep-ai-btn">
          {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Generate steps
        </Button>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Canvas: Node-based flow */}
        <div ref={canvasRef} className="flex-1 overflow-auto relative bg-[repeating-linear-gradient(0deg,transparent,transparent_19px,var(--border)_19px,var(--border)_20px),repeating-linear-gradient(90deg,transparent,transparent_19px,var(--border)_19px,var(--border)_20px)] bg-[length:20px_20px] [background-position:center] opacity-[0.97]" data-testid="pipeline-canvas">
          <div className="flex flex-col items-center py-8 min-h-full">
            {steps.map((step, idx) => {
              const stepDef = STEP_TYPES.find(s => s.type === step.type);
              const StepIcon = stepDef?.icon || Database;
              const isSelected = selectedNode === idx;
              const style = NODE_STYLES[step.type] || NODE_STYLES.source;
              const isConfigured = (step.type === 'source' && (step.collection || step.model_id))
                || (step.type === 'filter' && step.field)
                || (step.type === 'rename' && (step.old_name || (step.renames && step.renames[0]?.old_name)))
                || (step.type === 'derive' && step.name && step.expression)
                || (step.type === 'sort' && step.field)
                || (step.type === 'limit' && step.count)
                || (step.type === 'join' && step.collection && step.left_key && step.right_key)
                || (step.type === 'select' && (step.fields || []).length > 0)
                || (step.type === 'cast' && (step.field || (step.casts && step.casts[0]?.field)))
                || (step.type === 'fill_null' && (step.field || (step.fills && step.fills[0]?.field)))
                || (step.type === 'union' && step.collection);
              return (
                <React.Fragment key={idx}>
                  {/* Node */}
                  <div
                    ref={el => { nodeRefs.current[idx] = el; }}
                    className={`group relative w-64 px-4 py-3.5 rounded-xl border-2 cursor-pointer transition-all select-none ${
                      isSelected
                        ? `${style.border} ${style.bg} shadow-[0_0_24px_rgba(99,102,241,0.2)] ring-2 ${style.ring}`
                        : 'border-border/40 bg-background/80 backdrop-blur-sm hover:border-primary/40 hover:shadow-md'
                    }`}
                    onClick={() => setSelectedNode(isSelected ? null : idx)}
                    data-testid={`canvas-node-${idx}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border ${isSelected ? style.iconBg : 'bg-primary/10 border-primary/20'}`}>
                        <StepIcon className={`w-[18px] h-[18px] ${isSelected ? style.icon : 'text-primary'}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <Badge className="text-[8px] bg-muted/50 text-muted-foreground">{idx + 1}</Badge>
                          <p className="text-xs font-semibold text-foreground capitalize">{step.type.replace('_', ' ')}</p>
                          {!isConfigured && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" title="Needs configuration" />}
                        </div>
                        <p className="text-[9px] text-muted-foreground truncate mt-0.5">
                          {step.type === 'source' ? (step.collection || step.model_id || 'Configure source...') :
                           step.type === 'filter' ? `${step.field || '...'} ${step.operator || ''} ${step.value ?? ''}` :
                           step.type === 'rename' ? `${step.old_name || step.renames?.[0]?.old_name || '...'} → ${step.new_name || step.renames?.[0]?.new_name || '...'}` :
                           step.type === 'derive' ? (step.name || 'Configure...') :
                           step.type === 'sort' ? `${step.field || step.sorts?.[0]?.field || '...'} ${step.direction || step.sorts?.[0]?.direction || 'asc'}` :
                           step.type === 'limit' ? `${step.count || 100} rows` :
                           step.type === 'join' ? `${step.join_type || 'left'} join on ${step.left_key || '...'}` :
                           step.type === 'select' ? `${(step.fields || []).length} columns` :
                           stepDef?.desc || 'Configure...'}
                        </p>
                      </div>
                      {idx > 0 && (
                        <button onClick={e => { e.stopPropagation(); removeStep(idx); if (selectedNode === idx) setSelectedNode(null); else if (selectedNode > idx) setSelectedNode(selectedNode - 1); }} className="p-1 rounded hover:bg-destructive/10 text-muted-foreground/70 hover:text-destructive opacity-0 group-hover:opacity-100 transition-all" data-testid={`canvas-node-delete-${idx}`}><Trash2 className="w-3 h-3" /></button>
                      )}
                    </div>
                  </div>

                  {/* Connector */}
                  {idx < steps.length - 1 && (
                    <div className="flex flex-col items-center py-0.5">
                      <svg width="2" height="28" className="text-border/60"><line x1="1" y1="0" x2="1" y2="22" stroke="currentColor" strokeWidth="2" strokeDasharray="4,3" /><polygon points="0,22 2,28 4,22" fill="currentColor" /></svg>
                    </div>
                  )}
                </React.Fragment>
              );
            })}

            {/* Add Node Button */}
            <div className="flex flex-col items-center py-0.5 mt-0.5">
              <svg width="2" height="16" className="text-border/40"><line x1="1" y1="0" x2="1" y2="16" stroke="currentColor" strokeWidth="2" strokeDasharray="3,3" /></svg>
            </div>
            <button onClick={() => setAddStepOpen(true)} className="w-48 flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-border/30 hover:border-primary/40 text-muted-foreground hover:text-primary transition-all bg-background/50 backdrop-blur-sm" data-testid="add-step-btn">
              <Plus className="w-4 h-4" /> Add Step
            </button>
          </div>

          {/* Minimap */}
          <PipelineMiniMap
            steps={steps}
            selectedNode={selectedNode}
            canvasRef={canvasRef}
            scrollTick={steps.length}
            onJump={(idx) => {
              setSelectedNode(idx);
              const el = nodeRefs.current[idx];
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }}
          />
        </div>

        {/* Side Panel: Step Editor (slides in when a node is selected) */}
        {selectedNode !== null && (
          <div className="w-[380px] border-l border-border/50 flex flex-col overflow-hidden bg-background/95 backdrop-blur-md" data-testid="step-side-panel">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/50">
              <div className="flex items-center gap-2">
                <Badge className="text-[9px] bg-primary/20 text-primary">{selectedNode + 1}</Badge>
                <span className="text-sm font-heading font-semibold capitalize">{steps[selectedNode]?.type?.replace('_', ' ')}</span>
              </div>
              <button onClick={() => setSelectedNode(null)} className="p-1 rounded-lg hover:bg-muted text-muted-foreground"><X className="w-4 h-4" /></button>
            </div>
            <ScrollArea className="flex-1 p-4">
              <StepEditor
                step={steps[selectedNode]}
                index={selectedNode}
                onChange={s => updateStep(selectedNode, s)}
                onRemove={() => { removeStep(selectedNode); setSelectedNode(null); }}
                collections={collections}
                dataSources={dataSources}
                semanticModels={semanticModels}
                sourceFields={sourceFields}
                joinFields={joinFields}
                onIntrospect={introspectCollection}
                steps={steps}
              />
            </ScrollArea>
          </div>
        )}

        {/* Preview Panel (bottom or side) */}
        {showPreview && result?.data?.length > 0 && (
          <div className="w-[480px] border-l border-border/50 flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-border/50 bg-muted/20">
              <div className="flex items-center gap-2">
                <Table className="w-4 h-4 text-primary" />
                <span className="text-xs font-semibold">Preview</span>
                <span className="text-[10px] text-muted-foreground">{result.row_count} rows</span>
              </div>
              <button onClick={() => setShowPreview(false)} className="p-1 rounded hover:bg-muted text-muted-foreground"><X className="w-3.5 h-3.5" /></button>
            </div>
            <div className="flex-1 overflow-auto">
              <table className="w-full text-[10px]">
                <thead className="sticky top-0 z-10"><tr className="border-b border-border/50">{(result.columns || []).map(c => <th key={c} className="px-2 py-2 text-left text-[9px] font-semibold text-muted-foreground uppercase bg-background whitespace-nowrap">{c}</th>)}</tr></thead>
                <tbody>{result.data.slice(0, 100).map((row, i) => (<tr key={i} className="border-b border-border/10 hover:bg-muted/10">{(result.columns || []).map(c => <td key={c} className="px-2 py-1.5 font-mono text-foreground/80 whitespace-nowrap">{row[c] !== null && row[c] !== undefined ? String(row[c]) : ''}</td>)}</tr>))}</tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty preview placeholder */}
        {showPreview && (!result?.data?.length) && (
          <div className="w-[480px] border-l border-border/50 flex items-center justify-center">
            <div className="text-center">
              <Shuffle className="w-8 h-8 text-muted-foreground/20 mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">Run pipeline to see preview</p>
            </div>
          </div>
        )}
      </div>

      {/* Add Step Dialog */}
      <Dialog open={addStepOpen} onOpenChange={setAddStepOpen}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md max-h-[85vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-3 shrink-0 border-b border-border/30"><DialogTitle className="font-heading">Add Step</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-2 px-6 py-4 overflow-y-auto" data-testid="add-step-grid">
            {STEP_TYPES.map(st => (
              <button key={st.type} onClick={() => { addStep(st.type); setSelectedNode(steps.length); }} className="flex items-center gap-2 p-3 rounded-xl border border-border/30 hover:border-primary/30 hover:bg-primary/5 transition-all text-left active:scale-[0.98]" data-testid={`add-step-${st.type}`}>
                <st.icon className="w-4 h-4 text-primary shrink-0" />
                <div><p className="text-xs font-semibold text-foreground">{st.label}</p><p className="text-[9px] text-muted-foreground">{st.desc}</p></div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <UnsavedDialog />
      <SaveWithVersionDialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen} onSave={save} saving={saving} title={editingId ? 'Update Pipeline' : 'Save Pipeline'} />
    </div>
  );
}
